// Auto-hiding, elevation-aware application header, plus its pull tab.
//
// Three behaviors:
//
//   - Hide on scroll down, reveal on scroll up. The header stays put (never
//     hides) until the user has scrolled past its own height, so it doesn't
//     disappear on a two-pixel wobble right after landing on a page. Once
//     past that, any sustained downward scroll hides it; any upward scroll
//     reveals it immediately — the standard "pull down to bring it back"
//     pattern from iOS Safari / most modern app shells.
//
//   - Elevation: at the very top of the page the header sits flush, with no
//     shadow, reading as part of the page. Scrolled even slightly, it gains
//     a soft shadow and a more opaque frosted-glass background, reading as a
//     floating layer above the content.
//
//   - Pull tab: while hidden, a small tab is the only thing left at the top
//     edge. Hover it, click it, or drag it downward to bring the header back.
//     The tab lives outside the header element (it would otherwise translate
//     away with it), so this hook reaches for it by id.
//
// The header hides via `transform`, not by changing layout height, so the
// content underneath never reflows when it appears or disappears.

const HIDE_DELTA = 6; // px of sustained scroll before treating it as a direction
const ELEVATE_AT = 4; // px scrolled before the header "lifts" off the page
const PULL_DISTANCE = 12; // px of downward drag on the tab that counts as a pull

const AutoHideHeader = {
  mounted() {
    this.lastY = window.scrollY;
    this.hidden = false;
    this.elevated = false;
    this.ticking = false;
    this.puller = document.getElementById("dock-puller");

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.handleScroll();
        this.ticking = false;
      });
    };

    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.bindPuller();
    this.handleScroll();
  },

  destroyed() {
    window.removeEventListener("scroll", this.onScroll);
    this.unbindPuller();
    // Don't strand the flag on <html> for whatever renders next.
    document.documentElement.removeAttribute("data-chrome-hidden");
  },

  // ---------------------------------------------------------------------------
  // Scroll
  // ---------------------------------------------------------------------------

  handleScroll() {
    const y = window.scrollY;
    const delta = y - this.lastY;
    const headerHeight = this.el.offsetHeight;

    this.setElevated(y > ELEVATE_AT);

    if (y <= headerHeight) {
      this.setHidden(false);
    } else if (delta > HIDE_DELTA) {
      this.setHidden(true);
    } else if (delta < -HIDE_DELTA) {
      this.setHidden(false);
    }

    this.lastY = y;
  },

  setHidden(hidden) {
    if (hidden === this.hidden) return;
    this.hidden = hidden;
    this.el.classList.toggle("-translate-y-full", hidden);
    this.el.classList.toggle("translate-y-0", !hidden);

    if (this.puller) {
      if (hidden) {
        this.puller.setAttribute("data-visible", "");
      } else {
        this.puller.removeAttribute("data-visible");
      }
    }

    // Published on <html> so anything in the page can react to the header
    // going away — the sidebar uses it to fade in the platform mark, so the
    // top-left corner is never left blank and unbranded.
    document.documentElement.toggleAttribute("data-chrome-hidden", hidden);
  },

  setElevated(elevated) {
    if (elevated === this.elevated) return;
    this.elevated = elevated;
    this.el.classList.toggle("shadow-[0_8px_30px_-12px_rgba(15,23,42,0.18)]", elevated);
    this.el.classList.toggle("bg-white/90", elevated);
    this.el.classList.toggle("bg-white/70", !elevated);
  },

  // ---------------------------------------------------------------------------
  // Pull tab
  // ---------------------------------------------------------------------------

  bindPuller() {
    if (!this.puller) return;

    // Reveal, and re-baseline lastY so the very next scroll event isn't
    // measured against a stale position and immediately re-hides.
    this.reveal = () => {
      this.setHidden(false);
      this.lastY = window.scrollY;
    };

    this.onPullerEnter = () => this.reveal();
    this.onPullerClick = (e) => {
      e.preventDefault();
      this.reveal();
    };

    // Drag down on the tab. Pointer events cover mouse, pen and touch.
    this.onPullerDown = (e) => {
      this.pullStartY = e.clientY;
      this.puller.setPointerCapture?.(e.pointerId);
    };

    this.onPullerMove = (e) => {
      if (this.pullStartY == null) return;
      if (e.clientY - this.pullStartY > PULL_DISTANCE) {
        this.reveal();
        this.endPull(e);
      }
    };

    this.onPullerUp = (e) => this.endPull(e);

    this.puller.addEventListener("mouseenter", this.onPullerEnter);
    this.puller.addEventListener("click", this.onPullerClick);
    this.puller.addEventListener("pointerdown", this.onPullerDown);
    this.puller.addEventListener("pointermove", this.onPullerMove);
    this.puller.addEventListener("pointerup", this.onPullerUp);
    this.puller.addEventListener("pointercancel", this.onPullerUp);
  },

  endPull(e) {
    this.pullStartY = null;
    if (e && e.pointerId != null) {
      this.puller.releasePointerCapture?.(e.pointerId);
    }
  },

  unbindPuller() {
    if (!this.puller) return;
    this.puller.removeEventListener("mouseenter", this.onPullerEnter);
    this.puller.removeEventListener("click", this.onPullerClick);
    this.puller.removeEventListener("pointerdown", this.onPullerDown);
    this.puller.removeEventListener("pointermove", this.onPullerMove);
    this.puller.removeEventListener("pointerup", this.onPullerUp);
    this.puller.removeEventListener("pointercancel", this.onPullerUp);
  },
};

export default AutoHideHeader;
